import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { type AssetNetwork, TransactionOperation, TransactionStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, DateRangeFilterFields, enumOptions, SelectFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { CopyButton } from "@/components/copy-button";
import { HashLink } from "@/components/hash-link";
import { TRANSACTION_STATUS_COLOR, type StatusColor } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { cn, formatStatusLabel } from "@/lib/utils";
import { getTransaction, getTransactionAssetOptions, listTransactions } from "@/api/transactions";
import { listMerchants } from "@/api/merchants";
import {
  type ListTransactionsParams,
  type TransactionChainItem,
  type TransactionListItem,
  type TransactionParty,
} from "@/api/types";

// Task 30 Part C: the one transactions table, shared between
// `_app.transactions.tsx` (every operation, filterable) and
// `_app.unmatched-deposits.tsx` (DEPOSIT-with-no-invoice preset, locked).
// Colocated directly under routes/ (not components/) since it's route-
// specific UI wired to exactly two specific pages, not shared-kit UI —
// same `-` ignore-prefix convention Task 28's `-ledger-transactions-dialog.tsx`
// established (keeps this file out of TanStack Router's route generation;
// verified against `@tanstack/router-generator`'s `routeFileIgnorePrefix`
// default, unchanged since Task 28).
//
// Each of the two calling routes still declares its own `validateSearch` —
// search-param state can't be shared across two different route ids — but
// `useTableState` itself is called once, here, since this component is
// rendered directly by whichever single route mounts it (never both at once).

export interface TransactionsTableProps {
  /**
   * When true: the operation filter and the "unmatched" concept are hidden
   * entirely from the UI, the operation column is omitted (always DEPOSIT —
   * a redundant column), and every query is forced to `unmatched: true`,
   * `operation` omitted. The backend's own precedence rule (`unmatched` wins
   * over an explicit non-DEPOSIT `operation` — see
   * `ListTransactionsQueryDto`'s doc comment) means passing both is never
   * needed here.
   */
  lockedToUnmatched: boolean;
}

type TransactionFilters = {
  operation?: TransactionOperation;
  status?: TransactionStatus;
  assetId?: string;
  merchantId?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: TransactionFilters = {};

/** Search shape for both `/transactions` and `/unmatched-deposits` — `transactionId` is not a `listTransactions` filter, it's a deep-link hint the table uses to auto-open that row's detail dialog on mount (see the command palette's transaction search hits). */
export type TransactionsRouteSearch = TableSearch<TransactionFilters> & { transactionId?: string };

/** Plain type-guarded passthrough — mirrors _app.invoices.tsx's `validateSearch` house style. */
export function validateTransactionsSearch(search: Record<string, unknown>): TransactionsRouteSearch {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    operation: typeof search.operation === "string" ? (search.operation as TransactionOperation) : undefined,
    status: typeof search.status === "string" ? (search.status as TransactionStatus) : undefined,
    assetId: typeof search.assetId === "string" ? search.assetId : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
    transactionId: typeof search.transactionId === "string" ? search.transactionId : undefined,
  };
}

// ─── formatting helpers ─────────────────────────────────────────────────────

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function truncate(value: string): string {
  return value.length <= 16 ? value : `${value.slice(0, 8)}…${value.slice(-6)}`;
}

/**
 * `kind === null` means "no on-record counterpart row" — what that actually
 * is depends on the operation AND which side is being labeled (see
 * `TransactionOperation`'s own doc comments): a DEPOSIT's source and a
 * REFUND/WITHDRAW's destination are the external customer/payout address, a
 * GAS_FUND's source is always the platform gas vault, and a GAS_RECLAIM's
 * destination is always the platform gas vault too (GAS_RECLAIM runs the
 * opposite direction of GAS_FUND: wallet → gas vault, not gas vault →
 * wallet).
 *
 * `side` exists specifically because of feature-035's batched gas funding:
 * a batched GAS_FUND's Fireblocks transfer fans out to several destination
 * wallets, so `TransactionClassifierService` deliberately leaves
 * `destinationWallet` (and thus `party.kind`) null on THAT side too — there
 * is no single wallet to resolve. Without `side` here, that null-kind
 * destination fell through to the exact same case as the GAS_FUND's
 * genuinely-gas-station SOURCE, so a batched transfer rendered
 * "Gas Station → Gas Station" — wrong on the destination side, which is
 * really "several of our own wallets," never the gas station itself.
 *
 * Each route entity type gets its own fixed color (reused by both the Route
 * column's badge and the detail dialog's PartyDetail card), so the same kind
 * reads as the same color everywhere it appears: Account (on-platform,
 * primary) = info, Transient wallet (on-platform, secondary) = purple,
 * Channel (on-platform, its own deposit address — feature-031) = success,
 * Gas Station (internal infra) = warning, Batch destinations (on-platform,
 * several wallets — feature-035) = neutral, External (off-platform) =
 * neutral.
 */
function partyEntityType(
  party: TransactionParty,
  operation: TransactionOperation | undefined,
  side: "source" | "destination",
): { label: string; color: StatusColor } {
  if (party.kind === "ACCOUNT") return { label: "Account", color: "info" };
  if (party.kind === "TRANSIENT_WALLET") return { label: "Transient wallet", color: "purple" };
  if (party.kind === "CHANNEL") return { label: "Channel", color: "success" };
  if (operation === TransactionOperation.GAS_FUND) {
    // Batched gas funding (feature-035): the real destination is several
    // wallets, not the gas station — see this invoice's own
    // `gas_fund_request` rows for the per-wallet breakdown.
    return side === "source"
      ? { label: "Gas Station", color: "warning" }
      : { label: "Batch destinations", color: "neutral" };
  }
  if (operation === TransactionOperation.GAS_RECLAIM && side === "destination") {
    return { label: "Gas Station", color: "warning" };
  }
  switch (operation) {
    case TransactionOperation.DEPOSIT:
    case TransactionOperation.REFUND:
    case TransactionOperation.WITHDRAW:
      return { label: "External", color: "neutral" };
    default:
      return { label: "—", color: "neutral" };
  }
}

// ─── row-detail dialog ──────────────────────────────────────────────────────
//
// Fetch-on-open, same convention as every other detail dialog in this app
// (e.g. -ledger-transactions-dialog.tsx, _app.high-risk.tsx's
// CaseDetailDialog): a `useQuery` keyed on the selected id, `enabled` only
// while a row is selected.

function ReplacementChainList({ chain, symbol }: { chain: TransactionChainItem[]; symbol: string }) {
  if (chain.length <= 1) {
    return <p className="text-sm text-muted-foreground">Not part of an RBF replacement chain.</p>;
  }

  return (
    <ol className="space-y-2">
      {chain.map((entry, index) => (
        <li
          key={entry.id}
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
            entry.isCurrent ? "border-foreground/40 bg-muted font-medium" : "border-border bg-background",
          )}
        >
          <span className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">#{index + 1}</span>
            <span className="font-mono text-xs">{truncate(entry.id)}</span>
            <StatusBadge label={formatStatusLabel(entry.status)} color={TRANSACTION_STATUS_COLOR[entry.status]} />
            {entry.isCurrent && <span className="text-xs text-muted-foreground">(you are here)</span>}
          </span>
          <span className="flex items-center gap-3 text-xs text-muted-foreground">
            <Amount native={entry.amount} symbol={symbol} usd={entry.amountUsd} inline />
            <span>{formatTimestamp(entry.chainStartedAt)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

/** Uppercase eyebrow label above a group of `DetailRow`s — same treatment as the Replacement chain heading. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

/** Bordered card wrapper shared by the Details/Timeline/Replacement-chain sections — gives each block a visual edge instead of a bare heading + flat list. */
function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <SectionLabel>{title}</SectionLabel>
      {children}
    </div>
  );
}

/** A chronological event list rendered as a connected vertical stepper — genuinely sequential data, unlike a plain key/value list. */
function Timeline({ entries }: { entries: Array<{ label: string; value: string }> }) {
  return (
    <ol>
      {entries.map((entry, index) => (
        <li key={entry.label} className="relative flex gap-3 pb-4 pl-0.5 last:pb-0">
          {index < entries.length - 1 && (
            <span className="absolute left-[3.5px] top-3 h-full w-px bg-border" aria-hidden="true" />
          )}
          <span className="relative top-1.5 h-[7px] w-[7px] shrink-0 rounded-full bg-foreground/50" />
          <div className="flex flex-1 items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{entry.label}</span>
            <span className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
              {entry.value}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** One side of the Route section — entity-type badge, merchant name, plus address + explorer link + copy button when known. */
function PartyDetail({
  party,
  operation,
  network,
  side,
}: {
  party: TransactionParty;
  operation: TransactionOperation;
  network: AssetNetwork;
  side: "source" | "destination";
}) {
  const entityType = partyEntityType(party, operation, side);
  return (
    <div className="flex-1 space-y-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <StatusBadge label={entityType.label} color={entityType.color} />
        {party.merchantBusinessName && (
          <span className="text-sm text-muted-foreground/90">{party.merchantBusinessName}</span>
        )}
      </div>
      {party.address ? (
        <div className="text-xs">
          <HashLink value={party.address} type="address" network={network} />
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">No on-record address</div>
      )}
    </div>
  );
}

function TransactionDetailDialog({
  transactionId,
  onOpenChange,
}: {
  transactionId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["transaction", transactionId],
    queryFn: () => getTransaction(transactionId!),
    enabled: transactionId !== null,
  });

  return (
    <Dialog open={transactionId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {isPending && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {isError && !isPending && (
          <div className="py-10 text-center text-sm text-muted-foreground">Failed to load transaction.</div>
        )}

        {data && !isPending && !isError && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
                <DialogTitle asChild>
                  <span className="flex items-center gap-1.5 font-mono text-base">
                    {truncate(data.fireblocksTxId)}
                    <CopyButton
                      value={data.fireblocksTxId}
                      label="Copy Fireblocks transaction ID"
                      className="h-4 w-4 text-muted-foreground hover:text-foreground"
                    />
                  </span>
                </DialogTitle>
                <StatusBadge label={formatStatusLabel(data.status)} color={TRANSACTION_STATUS_COLOR[data.status]} />
              </div>
              <DialogDescription>{formatStatusLabel(data.operation)}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-end justify-between gap-4 border-b pb-4">
              <div>
                <div className="text-2xl font-semibold tracking-tight">
                  <Amount native={data.amount} symbol={data.asset.symbol} usd={data.amountUsd} inline />
                </div>
                <div className="mt-1">
                  <AssetLabel symbol={data.asset.symbol} network={data.asset.network} inline />
                </div>
              </div>
              {(data.isReplacement || data.wasReplaced) && (
                <StatusBadge
                  label={data.wasReplaced ? "Replaced" : "Replacement"}
                  color={data.wasReplaced ? "neutral" : "info"}
                />
              )}
            </div>

            <div className="space-y-2">
              <SectionLabel>Route</SectionLabel>
              <div className="flex items-center gap-2">
                <PartyDetail party={data.source} operation={data.operation} network={data.asset.network} side="source" />
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                <PartyDetail
                  party={data.destination}
                  operation={data.operation}
                  network={data.asset.network}
                  side="destination"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SectionCard title="Details">
                <dl className="space-y-2">
                  <DetailRow
                    label="Tx hash"
                    value={
                      data.txHash ? (
                        <HashLink value={data.txHash} type="tx" network={data.asset.network} />
                      ) : (
                        "—"
                      )
                    }
                  />
                  <DetailRow
                    label="Network fee"
                    value={<Amount native={data.networkFee} symbol={data.feeCurrency ?? data.asset.symbol} usd={data.networkFeeUsd} inline />}
                  />
                  <DetailRow
                    label="Counterparty address"
                    value={
                      data.counterpartyAddress ? (
                        <HashLink value={data.counterpartyAddress} type="address" network={data.asset.network} />
                      ) : (
                        "—"
                      )
                    }
                  />
                  <DetailRow label="Linked invoice" value={data.invoicePublicId ?? "—"} />
                </dl>
              </SectionCard>

              <SectionCard title="Timeline">
                <Timeline
                  entries={[
                    { label: "Chain started", value: formatTimestamp(data.chainStartedAt) },
                    { label: "Fireblocks created", value: formatTimestamp(data.fireblocksCreatedAt) },
                    { label: "Last event", value: formatTimestamp(data.lastEventAt) },
                  ]}
                />
              </SectionCard>
            </div>

            <SectionCard title="Replacement chain">
              <ReplacementChainList chain={data.replacementChain} symbol={data.asset.symbol} />
            </SectionCard>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────
//
// DataTable has no onRowClick concept (out of scope to modify) — same
// cell-filling pattern as _app.high-risk.tsx's RowButton: a <button> calling
// back into the page's setSelectedId, since there's no route to navigate to
// here (just a dialog).

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

// ─── transactions table ─────────────────────────────────────────────────────

export function TransactionsTable({ lockedToUnmatched }: TransactionsTableProps) {
  const tableState = useTableState<TransactionFilters>({ defaultFilters: DEFAULT_FILTERS });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Deep-link support: the command palette's transaction search hits land on
  // `/transactions?transactionId=<id>` (no standalone detail route exists —
  // see search.service.ts's searchTransactions doc comment). `strict: false`
  // since this component is shared between two different route ids (same
  // reason `useTableState` reads search this way) — not typed against either
  // route's `validateSearch` here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const routeSearch = useSearch({ strict: false } as any) as { transactionId?: string };
  useEffect(() => {
    if (routeSearch.transactionId) {
      setSelectedId(routeSearch.transactionId);
    }
  }, [routeSearch.transactionId]);

  const params: ListTransactionsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
    ...(lockedToUnmatched ? { operation: undefined, unmatched: true } : {}),
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["transactions", params],
    queryFn: () => listTransactions(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const { data: assetOptions } = useQuery({
    queryKey: ["transaction-asset-options"],
    queryFn: getTransactionAssetOptions,
  });

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<TransactionListItem>[] = [
    {
      key: "fireblocksTxId",
      header: "Tx Hash",
      // Not a RowButton: the hash itself is a clickable explorer link (via
      // HashLink), same as every other Tx hash/Address column in the app —
      // detail-dialog access lives on the other columns in this row instead.
      cell: (t) =>
        t.txHash ? (
          <HashLink value={t.txHash} type="tx" network={t.asset.network} className="text-xs" />
        ) : (
          <RowButton onSelect={() => setSelectedId(t.id)}>
            <span className="font-mono text-xs">-</span>
          </RowButton>
        ),
    },
    ...(lockedToUnmatched
      ? [
          // Every row here is a DEPOSIT (the Operation column would be
          // redundant, hence hidden — see the prop's doc comment), so this
          // slot is spent on the one field an investigator actually needs:
          // which external address sent an amount nothing could be matched
          // to. Route already names the DEPOSIT's source "External"; this
          // is the address behind that label.
          {
            key: "counterpartyAddress",
            header: "Counterparty",
            // Not a RowButton: the address itself is a clickable explorer
            // link (via HashLink) — see the Tx Hash column's identical note.
            cell: (t: TransactionListItem) =>
              t.counterpartyAddress ? (
                <HashLink value={t.counterpartyAddress} type="address" network={t.asset.network} className="text-xs" />
              ) : (
                <RowButton onSelect={() => setSelectedId(t.id)}>
                  <span className="text-muted-foreground">—</span>
                </RowButton>
              ),
          } as DataTableColumn<TransactionListItem>,
        ]
      : [
          {
            key: "operation",
            header: "Operation",
            filter: columnFilter(tableState, ["operation"], (draft, set) => (
              <SelectFilterField
                id="filter-operation"
                label="Operation"
                allLabel="All operations"
                options={enumOptions(Object.values(TransactionOperation))}
                value={draft.operation}
                onChange={(v) => set("operation", v as TransactionOperation | undefined)}
              />
            )),
            cell: (t: TransactionListItem) => (
              <RowButton onSelect={() => setSelectedId(t.id)}>{formatStatusLabel(t.operation)}</RowButton>
            ),
          } as DataTableColumn<TransactionListItem>,
        ]),
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(TransactionStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as TransactionStatus | undefined)}
        />
      )),
      cell: (t) => (
        <RowButton onSelect={() => setSelectedId(t.id)}>
          <StatusBadge label={formatStatusLabel(t.status)} color={TRANSACTION_STATUS_COLOR[t.status]} />
        </RowButton>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      filter: columnFilter(tableState, ["assetId"], (draft, set) => (
        <SelectFilterField
          id="filter-asset"
          label="Asset"
          allLabel="All assets"
          options={(assetOptions ?? []).map((a) => ({
            value: a.id,
            label: `${a.symbol} · ${formatStatusLabel(a.network)}`,
          }))}
          value={draft.assetId}
          onChange={(v) => set("assetId", v)}
        />
      )),
      cell: (t) => (
        <RowButton onSelect={() => setSelectedId(t.id)}>
          <AssetLabel symbol={t.asset.symbol} network={t.asset.network} />
        </RowButton>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      cell: (t) => (
        <RowButton onSelect={() => setSelectedId(t.id)}>
          <Amount native={t.amount} symbol={t.asset.symbol} usd={t.amountUsd} />
        </RowButton>
      ),
    },
    {
      key: "route",
      header: "Route",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (t) => {
        const source = partyEntityType(t.source, t.operation, "source");
        const destination = partyEntityType(t.destination, t.operation, "destination");
        return (
          <RowButton onSelect={() => setSelectedId(t.id)}>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <StatusBadge label={source.label} color={source.color} />
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <StatusBadge label={destination.label} color={destination.color} />
            </span>
          </RowButton>
        );
      },
    },
    {
      key: "chainStartedAt",
      header: "Started",
      filter: columnFilter(tableState, ["from", "to"], (draft, set) => (
        <DateRangeFilterFields
          idPrefix="filter-date"
          from={draft.from}
          to={draft.to}
          onFromChange={(v) => set("from", v)}
          onToChange={(v) => set("to", v)}
        />
      )),
      cell: (t) => <RowButton onSelect={() => setSelectedId(t.id)}>{formatTimestamp(t.chainStartedAt)}</RowButton>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load transactions." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(t) => t.id}
        emptyMessage={
          lockedToUnmatched ? "No unmatched deposits match your filters." : "No transactions match your filters."
        }
      />

      <TransactionDetailDialog
        transactionId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
